import { useState } from 'react';
import { formatRoute } from '../../app/router';
import { Empty, errorView, Loading } from '../../states/States';
import { useLoad } from '../../views/useLoad';
import { useAppServices } from '../../views/ViewContext';
import { forUsers, formatDate, text, type CompositionLayer } from './tenant-data';

/**
 * Composition du modèle (ADR-128 §7) : des bandes empilées, du client en haut au Core en bas, une carte
 * par couche (libellé, version, ce qu'elle étend), et un panneau de détail (publication, approbateur,
 * dépendances, remplacement, historique). Éléments HTML accessibles, sans bibliothèque de graphiques.
 * L'identifiant technique d'un Pack n'apparaît que dans le détail.
 */
const BANDS: ReadonlyArray<{ kind: CompositionLayer['kind']; title: string; hint: string }> = [
  { kind: 'customer', title: 'Client', hint: 'modèle du client' },
  { kind: 'extension', title: 'Extensions', hint: 'Packs qui en étendent un autre' },
  { kind: 'pack', title: 'Packs', hint: 'métier et présentation' },
  { kind: 'core', title: 'Core', hint: 'socle générique' },
];

const nameOf = (layer: CompositionLayer): string => text(layer.label, layer.lineage.replace(/^(pack|customer):/, ''));

export function CompositionTab() {
  const services = useAppServices();
  const [loaded, reload] = useLoad<CompositionLayer[]>('tenant-composition', async () => (await services.client.query<{ layers: CompositionLayer[] }>('GetTenantComposition')).data.layers);
  const [picked, setPicked] = useState<string | null>(null);
  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  if (loaded.value.length === 0) return <Empty label="Aucune couche active." />;
  const layers = loaded.value;
  const byLineage = new Map(layers.map((layer) => [layer.lineage, layer]));
  const selected = layers.find((layer) => layer.lineage === picked) ?? layers.find((layer) => layer.kind === 'customer') ?? layers[0]!;
  const extendsOf = (layer: CompositionLayer): string[] =>
    layer.dependencies.filter((dependency) => dependency.lineage !== 'core').map((dependency) => {
      const target = byLineage.get(dependency.lineage);
      return target === undefined ? dependency.lineage : nameOf(target);
    });
  return (
    <div className="ay-tenant__composition">
      <section className="ay-tenant__bands" aria-label="Couches du modèle">
        {BANDS.map((band, index) => {
          const members = layers.filter((layer) => layer.kind === band.kind);
          if (members.length === 0) return null;
          return (
            <div key={band.kind} className="ay-tenant__bandrow">
              <div className="ay-tenant__bandlabel">
                <strong>{band.title}</strong>
                <span>{band.hint}</span>
              </div>
              <ul className={`ay-tenant__band ay-tenant__band--${band.kind} ay-tenant__band--step${index}`}>
                {members.map((layer) => {
                  const extended = extendsOf(layer);
                  return (
                    <li key={layer.lineage}>
                      <button type="button" className="ay-tenant__layer" aria-pressed={layer.lineage === selected.lineage} onClick={() => setPicked(layer.lineage)}>
                        <span className="ay-tenant__layertitle">
                          <strong>{nameOf(layer)}</strong>
                          <span className="ay-tenant__version">{layer.version}</span>
                        </span>
                        {layer.kind !== 'pack' && layer.kind !== 'core' && extended.length > 0 && <span className="ay-tenant__extends">↳ étend {extended.join(', ')}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
        <p className="ay-text ay-text--muted ay-tenant__bandnote">Chaque couche ne surcharge que ce que la couche du dessous déclare surchargeable. La plus haute l’emporte.</p>
        {/* Atlas du modèle (ADR-130 tranche 1) : le modèle effectif que ces couches composent ; la page est autorisée au serveur. */}
        <p className="ay-tenant__bandnote">
          <a
            href={formatRoute({ kind: 'model' })}
            onClick={(event) => {
              event.preventDefault();
              services.navigate({ kind: 'model' });
            }}
          >
            Voir le modèle effectif
          </a>
        </p>
      </section>

      <aside className="ay-card ay-tenant__detail" aria-labelledby="ay-tenant-layer">
        <span className="ay-tenant__eyebrow">{BANDS.find((band) => band.kind === selected.kind)?.title ?? selected.kind}</span>
        <h2 id="ay-tenant-layer">{nameOf(selected)}</h2>
        <span className="ay-tenant__mono ay-text--muted">
          {selected.lineage}@{selected.version}
        </span>
        {selected.description !== undefined && <p>{forUsers(text(selected.description))}</p>}
        <dl className="ay-tenant__facts">
          <dt>Version active</dt>
          <dd className="ay-tenant__mono">{selected.version}</dd>
          <dt>Publiée le</dt>
          <dd>{formatDate(selected.published_at)}</dd>
          <dt>Approuvée par</dt>
          <dd>{selected.published_by ?? '—'}</dd>
          <dt>Dépend de</dt>
          <dd className="ay-tenant__mono">{selected.dependencies.length === 0 ? '—' : selected.dependencies.map((dependency) => `${dependency.lineage} ${dependency.range}`).join(', ')}</dd>
          {selected.replaces !== undefined && (
            <>
              <dt>Remplace</dt>
              <dd className="ay-tenant__mono">
                {selected.replaces.lineage}
                {selected.replaces.version === undefined ? ` ${selected.replaces.range}` : ` ${selected.replaces.version}`}
              </dd>
            </>
          )}
        </dl>
        <div>
          <strong>Historique</strong>
          <ol className="ay-tenant__history">
            <li>
              <span className="ay-tenant__mono">{selected.version}</span> {formatDate(selected.published_at)} <span className="ay-text--muted">version active</span>
            </li>
            {selected.previous.map((entry) => (
              <li key={entry.version}>
                <span className="ay-tenant__mono">{entry.version}</span> {formatDate(entry.published_at)} {entry.published_by === undefined ? null : <span className="ay-text--muted">par {entry.published_by}</span>}
              </li>
            ))}
            {selected.previous_count > selected.previous.length && <li className="ay-text--muted">… et {selected.previous_count - selected.previous.length} versions plus anciennes</li>}
          </ol>
        </div>
      </aside>
    </div>
  );
}
